// Preferência de cookies compartilhada entre B2B (site-institucional) e B2C
// (ecommerce, checkout).
//
// A escolha fica em um cookie no domínio caixaprepagos.com.br — visível em todos
// os subdomínios — em vez de localStorage, que é isolado por origem. Este
// arquivo é idêntico nos três projetos; ao alterá-lo, replique nos outros.
//
// Fluxo combinado com o consultor de tags:
// - carregamento completo: o script inline do <head> (consentBootstrapScript)
//   lê o cookie e, havendo escolha, envia `cookie_consent_update` ao Data Layer;
// - escolha no banner: grava o cookie e envia o mesmo evento (pushConsentUpdate);
// - troca de rota da SPA: nada é reenviado (o script do <head> não roda de novo).
// O GTM aplica o Consent Mode a partir desse evento.
//
// Assim como consent-config.ts, NÃO marcar com 'use client': o script do <head>
// é montado em componente de servidor.

import {
  CONSENT_EXPIRY_MS,
  POLICY_VERSION,
  STORAGE_KEY,
  STORAGE_VERSION,
} from './consent-config';

export const CONSENT_COOKIE_NAME = 'ccp_consent';
export const CONSENT_COOKIE_DOMAIN = 'caixaprepagos.com.br';
export const CONSENT_UPDATE_EVENT = 'cookie_consent_update';

const CONSENT_COOKIE_MAX_AGE_S = Math.floor(CONSENT_EXPIRY_MS / 1000);

export type ConsentChoice = {
  analytics: boolean;
  marketing: boolean;
};

export type StoredConsentChoice = ConsentChoice & { decidedAt: number };

// Valor do cookie: "<versão>.<versão da política>.<analytics>.<marketing>.<decidido em ms>"
// ex.: "1.1.1.0.1760968800000" — analytics autorizado, publicidade recusada.
export function serializeConsent(
  choice: ConsentChoice,
  decidedAt: number
): string {
  return [
    STORAGE_VERSION,
    POLICY_VERSION,
    choice.analytics ? 1 : 0,
    choice.marketing ? 1 : 0,
    decidedAt,
  ].join('.');
}

export function parseConsent(
  value: string | null | undefined,
  now: number = Date.now()
): StoredConsentChoice | null {
  if (!value) return null;
  const parts = value.split('.');
  if (parts.length !== 5) return null;
  if (Number(parts[0]) !== STORAGE_VERSION) return null;
  if (Number(parts[1]) !== POLICY_VERSION) return null;
  const decidedAt = Number(parts[4]);
  if (!decidedAt || now - decidedAt > CONSENT_EXPIRY_MS) return null;
  return {
    analytics: parts[2] === '1',
    marketing: parts[3] === '1',
    decidedAt,
  };
}

// Em localhost, *.azurestaticapps.net etc. o cookie fica restrito ao host.
export function consentCookieDomain(hostname: string): string | null {
  return hostname === CONSENT_COOKIE_DOMAIN ||
    hostname.endsWith(`.${CONSENT_COOKIE_DOMAIN}`)
    ? CONSENT_COOKIE_DOMAIN
    : null;
}

export function readConsentCookie(): StoredConsentChoice | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${CONSENT_COOKIE_NAME}=`;
  const entry = document.cookie
    .split('; ')
    .find((item) => item.startsWith(prefix));
  return parseConsent(entry?.slice(prefix.length));
}

export function writeConsentCookie(
  choice: ConsentChoice,
  decidedAt: number = Date.now()
): void {
  if (typeof document === 'undefined') return;
  const domain = consentCookieDomain(window.location.hostname);
  document.cookie = [
    `${CONSENT_COOKIE_NAME}=${serializeConsent(choice, decidedAt)}`,
    'path=/',
    `max-age=${CONSENT_COOKIE_MAX_AGE_S}`,
    'samesite=lax',
    domain ? `domain=${domain}` : '',
    window.location.protocol === 'https:' ? 'secure' : '',
  ]
    .filter(Boolean)
    .join('; ');
}

export function pushConsentUpdate(choice: ConsentChoice): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: CONSENT_UPDATE_EVENT,
    consent_analytics: choice.analytics ? 'granted' : 'denied',
    consent_marketing: choice.marketing ? 'granted' : 'denied',
  });
}

// Script inline do <head>, executado uma vez por carregamento completo, antes
// do GTM. Repete em ES5 a lógica de parseConsent/writeConsentCookie acima —
// manter os dois em sincronia (há testes cobrindo ambos).
//
// Também migra a escolha antiga, salva em localStorage pelo banner anterior,
// para o cookie, para que quem já decidiu não veja o banner de novo.
//
// `beforePush` recebe a variável `c` ({ analytics, marketing } ou null) e roda
// antes do envio do evento.
export function consentBootstrapScript(beforePush = ''): string {
  return `(function(){
  window.dataLayer = window.dataLayer || [];
  var NAME = '${CONSENT_COOKIE_NAME}', DOMAIN = '${CONSENT_COOKIE_DOMAIN}';
  var V = ${STORAGE_VERSION}, PV = ${POLICY_VERSION}, EXP = ${CONSENT_EXPIRY_MS};
  function parse(v) {
    if (!v) return null;
    var p = v.split('.');
    if (p.length !== 5 || Number(p[0]) !== V || Number(p[1]) !== PV) return null;
    var t = Number(p[4]);
    if (!t || Date.now() - t > EXP) return null;
    return { analytics: p[2] === '1', marketing: p[3] === '1', decidedAt: t };
  }
  function read() {
    var items = document.cookie.split('; ');
    for (var i = 0; i < items.length; i++) {
      if (items[i].indexOf(NAME + '=') === 0) return parse(items[i].slice(NAME.length + 1));
    }
    return null;
  }
  var c = null;
  try {
    c = read();
    if (!c) {
      var raw = localStorage.getItem('${STORAGE_KEY}');
      var old = raw && JSON.parse(raw);
      var t = old && Date.parse(old.decidedAt);
      if (old && old.state && old.version === V && old.policyVersion === PV
        && t && Date.now() - t <= EXP) {
        var h = location.hostname;
        var shared = h === DOMAIN || h.slice(-(DOMAIN.length + 1)) === '.' + DOMAIN;
        document.cookie = NAME + '=' + [V, PV, old.state.analytics ? 1 : 0, old.state.marketing ? 1 : 0, t].join('.')
          + '; path=/; max-age=' + Math.floor(EXP / 1000) + '; samesite=lax'
          + (shared ? '; domain=' + DOMAIN : '')
          + (location.protocol === 'https:' ? '; secure' : '');
        c = read();
        if (c) localStorage.removeItem('${STORAGE_KEY}');
      }
    }
  } catch (e) {}
  ${beforePush}
  if (c) {
    window.dataLayer.push({
      event: '${CONSENT_UPDATE_EVENT}',
      consent_analytics: c.analytics ? 'granted' : 'denied',
      consent_marketing: c.marketing ? 'granted' : 'denied'
    });
  }
})();`;
}
